import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  #buyButtonsIntersectionObserver = null;
  #mainBottomObserver = null;
  #resetTimeout;
  #isStuck = false;
  #animationTimeout = null;
  #abortController = new AbortController();
  #targetAddToCartButton = null;
  #currentQuantity = 1;
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    // IMPORTANT: make sure the component is not trapped inside a transformed wrapper
    // (Horizon sometimes uses transforms on page wrappers)
    if (this.parentElement !== document.body) {
      // Keep a placeholder so nothing breaks in layout (optional, safe)
      const ph = document.createElement('div');
      ph.setAttribute('data-sticky-atc-placeholder', '');
      ph.style.display = 'contents';
      this.parentElement?.insertBefore(ph, this);
      document.body.appendChild(this);
    }

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;

    // Instead of closest('.shopify-section'), bind events from the section that contains the product form
    const sectionEl = this.#getSectionElement();
    sectionEl?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    sectionEl?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
  }

  #getSectionElement() {
    const productForm = this.#getProductForm();
    return productForm ? productForm.closest('.shopify-section') : null;
  }

  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    // Find product form anywhere (works even if sticky element is moved to body)
    const form =
      document.querySelector(`product-form-component[data-product-id="${productId}"]`) ||
      document.querySelector(`form[action*="/cart/add"][data-product-id="${productId}"]`) ||
      null;

    return form;
  }

  #getBuyButtonsTarget(productForm) {
    // Prefer Horizon container if present
    let target =
      productForm.closest('.buy-buttons-block') ||
      productForm.querySelector('.buy-buttons-block');

    // Fallbacks
    if (!target) {
      target =
        productForm.querySelector('[data-buy-buttons]') ||
        productForm.querySelector('[ref="addToCartButton"]') ||
        productForm.querySelector('button[type="submit"][name="add"]') ||
        productForm.querySelector('button[name="add"]');
    }

    return target instanceof HTMLElement ? target : null;
  }

  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyTarget = this.#getBuyButtonsTarget(productForm);
    if (!buyTarget) return;

    const footer =
      document.querySelector('footer') ||
      document.querySelector('[class*="footer-group"]') ||
      document.querySelector('[data-footer]') ||
      null;

    if (!footer) return;

    // Observe buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      if (!entry.isIntersecting && !this.#isStuck) {
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) this.#showStickyBar();
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observe footer visibility to hide sticky at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          const rect = buyTarget.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      { rootMargin: '200px 0px 0px 0px' }
    );

    this.#buyButtonsIntersectionObserver.observe(buyTarget);
    this.#mainBottomObserver.observe(footer);

    this.#targetAddToCartButton =
      productForm.querySelector('[ref="addToCartButton"]') ||
      productForm.querySelector('button[type="submit"][name="add"]') ||
      productForm.querySelector('button[name="add"]');
  }

  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;

    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();

    const cartIcon = document.querySelector('.header-actions__cart-icon');
    if (this.refs.addToCartButton.dataset.added !== 'true') this.refs.addToCartButton.dataset.added = 'true';
    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;

    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = document.createElement('fly-to-cart');
    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    if (variant && variant.id) this.dataset.currentVariantId = variant.id;

    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton =
        productForm.querySelector('[ref="addToCartButton"]') ||
        productForm.querySelector('button[type="submit"][name="add"]') ||
        productForm.querySelector('button[name="add"]');
    }

    if (variant == null) this.#handleVariantUnavailable();
    this.#updateButtonText();
  };

  #handleVariantSelected = (event) => {
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => option.value)
      .filter((value) => value !== '')
      .join(' / ');

    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  #handleCartAddComplete = () => {
    if (this.#targetAddToCartButton) this.#targetAddToCartButton.dataset.puppet = 'false';
  };

  /** @param {QuantitySelectorUpdateEvent} event */
  #handleQuantityUpdate = (event) => {
    if (event.detail.cartLine) return;
    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  #showStickyBar() {
    this.#isStuck = true;
    this.refs.stickyBar.dataset.stuck = 'true';
  }

  #hideStickyBar() {
    this.#isStuck = false;
    this.refs.stickyBar.dataset.stuck = 'false';
  }

  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;
    const available = !addToCartButton.disabled;

    quantityNumber.textContent = this.#currentQuantity.toString();
    quantityDisplay.style.display = available && this.#currentQuantity > 1 ? 'inline' : 'none';
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
